import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Play, Star, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MovieDetails = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [movie, setMovie] = useState(null);
  const [trailers, setTrailers] = useState([]);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMovieDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/movies/${id}?language=${i18n.language}`);
      setMovie(response.data);

      // Extract trailers
      const videoResults = response.data.videos?.results || [];
      const movieTrailers = videoResults.filter(
        (v) => v.type === 'Trailer' && v.site === 'YouTube'
      );
      setTrailers(movieTrailers);

      // Auto-select first official trailer or first trailer
      const officialTrailer = movieTrailers.find((t) => t.official);
      setSelectedTrailer(officialTrailer || movieTrailers[0] || null);
    } catch (error) {
      console.error('Error fetching movie details:', error);
      toast.error('Failed to load movie details');
    } finally {
      setLoading(false);
    }
  }, [id, i18n.language]);

  useEffect(() => {
    fetchMovieDetails();
  }, [fetchMovieDetails]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7FFF7F] mx-auto mb-4"></div>
          <p className="text-gray-400">{t('movie.loading')}</p>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-gray-400">{t('movie.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="movie-details-page">
      {/* Backdrop Hero */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        {movie.backdrop_path && (
          <>
            <img
              src={`https://image.tmdb.org/t/p/original${movie.backdrop_path}`}
              alt={movie.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cinema-black via-cinema-black/70 to-transparent"></div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative -mt-32 px-6 md:px-12 max-w-7xl mx-auto pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Poster */}
          <div className="lg:col-span-1">
            {movie.poster_path && (
              <img
                src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                alt={movie.title}
                className="w-full rounded-xl shadow-2xl border border-white/10"
                data-testid="movie-poster"
              />
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1
                className="font-anton text-3xl md:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-4 leading-tight break-words"
                data-testid="movie-title"
              >
                {movie.title}
              </h1>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-cinema-gold fill-cinema-gold" />
                  <span className="text-white font-semibold text-lg">
                    {movie.vote_average?.toFixed(1)}
                  </span>
                </div>
                {movie.release_date && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-5 h-5" />
                    <span>{new Date(movie.release_date).getFullYear()}</span>
                  </div>
                )}
                {movie.runtime && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-5 h-5" />
                    <span>{movie.runtime} min</span>
                  </div>
                )}
              </div>

              {movie.genres && movie.genres.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre.id}
                      className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-sm text-gray-300 backdrop-blur-sm"
                    >
                      {genre.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-white mb-3">{t('movie.overview')}</h3>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                {movie.overview}
              </p>
            </div>

            {/* Trailer Player */}
            {selectedTrailer && (
              <div className="space-y-4" data-testid="trailer-section">
                <h3 className="text-2xl font-semibold text-white">{t('movie.watchTrailer')}</h3>
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedTrailer.key}`}
                    title={selectedTrailer.name}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    data-testid="youtube-player"
                  ></iframe>
                </div>
                <Button
                  variant="outline"
                  className="border-white/20 hover:bg-white/10 text-white"
                  onClick={() => {
                    const videoUrl = `https://www.youtube.com/watch?v=${selectedTrailer.key}`;
                    navigator.clipboard.writeText(videoUrl).then(() => {
                      toast.success('Video URL copied to clipboard!');
                    }).catch(() => {
                      toast.error('Failed to copy URL');
                    });
                  }}
                  data-testid="copy-url-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Copy Video URL
                </Button>
              </div>
            )}

            {/* Additional Trailers */}
            {trailers.length > 1 && (
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-white">{t('movie.moreTrailers')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {trailers
                    .filter((t) => t.key !== selectedTrailer?.key)
                    .map((trailer) => (
                      <Button
                        key={trailer.id}
                        variant="outline"
                        className="justify-start border-white/20 hover:bg-white/10 hover:border-cinema-red/50 text-white h-auto p-4"
                        onClick={() => setSelectedTrailer(trailer)}
                        data-testid={`trailer-${trailer.id}`}
                      >
                        <Play className="w-4 h-4 mr-3 flex-shrink-0 text-cinema-red" />
                        <span className="text-left truncate">{trailer.name}</span>
                      </Button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieDetails;